import { Component, OnInit, OnDestroy, Renderer2, Inject, PLATFORM_ID, NgZone, ElementRef, ViewChild } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { FormSubmissionService } from '../../_services/form-submission.service';
import { ThankYouTelemetryService } from '../../_services/thank-you-telemetry.service';
import { Subscription } from 'rxjs';

export interface Lead {
  Zip: string;
  State: string;
  CreditRating: string;
  ThanksSubID?: string;
}

interface MediaAlphaExchange {
  type: 'ad_unit';
  placement_id: string;
  placement_ids: {
    purchase: string;
    refinance: string;
  };
  sub_1: string;
  version: string;
  data: {
    zip: string;
    credit_rating: string;
    state: string;
  };
}

declare global {
  interface Window {
    MediaAlphaExchange?: MediaAlphaExchange;
  }
}

@Component({
  selector: 'app-thank-you-page-tandem',
  standalone: false,
  templateUrl: './thank-you-page-tandem.component.html',
  styleUrl: './thank-you-page-tandem.component.css'
})
export class ThankYouPageTandemComponent implements OnInit, OnDestroy {
  @ViewChild('mediaAlphaHost', { static: true }) private mediaAlphaHost?: ElementRef<HTMLElement>;

  readonly mediaAlphaContainerId = '__max__144652985';
  private readonly mediaAlphaScriptSrc = 'https://finance.mediaalpha.com/js/serve.js';
  private readonly mediaAlphaGeneratedIdPattern = /^__max__\d+$/;
  private mediaAlphaObserver?: MutationObserver;
  private mediaAlphaMoveTimers: number[] = [];
  private renderedMediaAlphaId?: string;
  private scriptEl?: HTMLScriptElement;
  private leadSubscription?: Subscription;
  private isBrowser = false;
  currentLead: any = null;

  constructor(
    private formSubmissionService: FormSubmissionService,
    public thankYouTelemetryService: ThankYouTelemetryService,
    private renderer: Renderer2,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
    private ngZone: NgZone
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    if (!this.isBrowser) {
      return;
    }

    this.leadSubscription = this.formSubmissionService.getLeadObservable().subscribe(lead => {
      if (lead && Object.keys(lead).length > 0) {
        this.currentLead = lead;
        this.ejecutarMediaAlpha(lead);
      }
      this.hideLoader();
    });
  }

  ngOnDestroy(): void {
    this.leadSubscription?.unsubscribe();

    if (!this.isBrowser) {
      return;
    }

    if (this.scriptEl?.parentNode) {
      this.renderer.removeChild(this.scriptEl.parentNode, this.scriptEl);
    }

    this.mediaAlphaObserver?.disconnect();
    this.mediaAlphaMoveTimers.forEach(timer => {
      this.document.defaultView?.clearTimeout(timer);
    });
  }

  ejecutarMediaAlpha(lead: Lead): void {
    if (!lead || Object.keys(lead).length === 0) {
      return;
    }

    const params = new URLSearchParams(this.document.location.search);
    const zip = lead.Zip || params.get('zip') || '';
    const state = lead.State || params.get('state') || '';
    let creditRating = lead.CreditRating || params.get('CreditRating') || '';
    if (!creditRating || creditRating.length < 3) {
      creditRating = 'GOOD';
    }
    const thanksSubID = lead.ThanksSubID || params.get('ThanksSubID') || 'CHS1';
    const win = this.document.defaultView;
    if (!win) {
      return;
    }

    win.MediaAlphaExchange = {
      type: 'ad_unit',
      placement_id: '6g8HJ4FMnT8fENFqqDKFfN2ZXbrdWg',
      placement_ids: {
        purchase: 'EiDpI4E_8JiEYFQk3fAfsVRXTRjCjQ',
        refinance: '6g8HJ4FMnT8fENFqqDKFfN2ZXbrdWg',
      },
      sub_1: thanksSubID,
      version: '17',
      data: {
        zip,
        credit_rating: creditRating,
        state,
      },
    };

    this.ensureMediaAlphaContainer();
    this.loadMediaAlphaScript();
  }

  private ensureMediaAlphaContainer(): void {
    const parent = this.mediaAlphaHost?.nativeElement;
    if (!parent) {
      return;
    }

    let container = parent.querySelector<HTMLElement>(`#${this.mediaAlphaContainerId}`);
    if (container) {
      container.innerHTML = '';
      return;
    }

    container = this.renderer.createElement('div') as HTMLDivElement;
    this.renderer.setAttribute(container, 'id', this.mediaAlphaContainerId);
    this.renderer.appendChild(parent, container);
  }

  private loadMediaAlphaScript(): void {
    this.ngZone.runOutsideAngular(() => {
      const existingScript = Array.from(this.document.getElementsByTagName('script')).find(script =>
        script.src.includes('finance.mediaalpha.com/js/serve.js')
      );

      if (existingScript?.parentNode) {
        this.renderer.removeChild(existingScript.parentNode, existingScript);
      }

      this.renderedMediaAlphaId = undefined;

      const script = this.renderer.createElement('script') as HTMLScriptElement;
      script.id = 'mediaalpha-finance-serve-script';
      script.type = 'text/javascript';
      script.src = this.mediaAlphaScriptSrc;
      script.async = true;
      script.onload = () => this.moveRenderedMediaAlphaIntoHost();
      this.scriptEl = script;

      this.renderer.appendChild(this.document.body, script);
      this.watchForRenderedMediaAlpha();
    });
  }

  private watchForRenderedMediaAlpha(): void {
    const win = this.document.defaultView;
    if (!win) {
      return;
    }

    this.mediaAlphaObserver?.disconnect();
    this.mediaAlphaMoveTimers.forEach(timer => win.clearTimeout(timer));
    this.mediaAlphaMoveTimers = [];

    this.mediaAlphaObserver = new MutationObserver(() => this.moveRenderedMediaAlphaIntoHost());
    this.mediaAlphaObserver.observe(this.document.body, { childList: true, subtree: true });

    [100, 300, 700, 1200, 2000, 3500].forEach(delay => {
      const timer = win.setTimeout(() => this.moveRenderedMediaAlphaIntoHost(), delay);
      this.mediaAlphaMoveTimers.push(timer);
    });

    const stopTimer = win.setTimeout(() => this.mediaAlphaObserver?.disconnect(), 5000);
    this.mediaAlphaMoveTimers.push(stopTimer);
  }

  private moveRenderedMediaAlphaIntoHost(): void {
    const parent = this.mediaAlphaHost?.nativeElement;
    if (!parent) {
      return;
    }

    const renderedElement = this.getRenderedMediaAlphaElement(parent);
    if (!renderedElement || parent.contains(renderedElement)) {
      return;
    }

    parent.querySelectorAll<HTMLElement>('[id^="__max__"]').forEach(element => {
      if (element !== renderedElement && element.parentNode && this.isEmptyElement(element)) {
        this.renderer.removeChild(element.parentNode, element);
      }
    });

    this.renderer.appendChild(parent, renderedElement);
  }

  private getRenderedMediaAlphaElement(parent: HTMLElement): HTMLElement | null {
    if (this.renderedMediaAlphaId) {
      const existingElement = this.document.getElementById(this.renderedMediaAlphaId);
      if (existingElement && !parent.contains(existingElement)) {
        return existingElement;
      }
    }

    const renderedElement = Array.from(this.document.querySelectorAll<HTMLElement>('[id^="__max__"]')).find(element =>
      this.mediaAlphaGeneratedIdPattern.test(element.id) && !parent.contains(element)
    );

    this.renderedMediaAlphaId = renderedElement?.id;
    return renderedElement || null;
  }

  private isEmptyElement(element: HTMLElement): boolean {
    return element.children.length === 0 && !element.textContent?.trim();
  }

  private hideLoader(): void {
    const loader = document.getElementById("loader");
    if (loader) {
      loader.style.display = "none";
    }
  }
}
